import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { shopApi } from "@/lib/api";

/** Rotating "Someone in Miami just bought X" toast in the bottom-left corner.
 *  Pulls from real paid orders (last 14 days). Hides entirely if the store has no orders yet
 *  so it never fakes social proof for a brand new store — the moment real orders roll in,
 *  it starts showing organically.
 */
export default function LiveOrderToast() {
  const { data = [] } = useQuery({
    queryKey: ["socialProof"],
    queryFn: () => shopApi.socialProof(20),
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000,
  });
  const [idx, setIdx] = useState(-1);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    if (data.length === 0) return;
    let cancelled = false;

    const cycle = async (i) => {
      if (cancelled) return;
      setIdx(i % data.length);
      // show for 5s, then hide for 3s, then next
      setTimeout(() => { if (!cancelled) setIdx(-1); }, 5000);
      setTimeout(() => { if (!cancelled) cycle(i + 1); }, 8000);
    };

    const kickoff = setTimeout(() => cycle(0), 6000);  // wait 6s after page load
    return () => { cancelled = true; clearTimeout(kickoff); };
  }, [data.length, dismissed]);

  const item = idx >= 0 ? data[idx] : null;
  if (dismissed || !item) return null;

  const relTime = (iso) => {
    try {
      const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs} h ago`;
      const days = Math.round(hrs / 24);
      return `${days} d ago`;
    } catch { return "just now"; }
  };

  const Content = (
    <div className="flex items-center gap-3">
      {item.product_image ? (
        <img src={item.product_image} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="h-11 w-11 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
          <ShoppingBag className="h-5 w-5 text-primary" />
        </div>
      )}
      <div className="min-w-0 pr-4">
        <p className="text-xs leading-tight">
          <span className="text-primary font-medium">{item.first_name}</span>
          <span className="text-muted-foreground"> in </span>
          <span className="text-white/90">{item.location}</span>
        </p>
        <p className="text-xs text-white/70 truncate">just bought {item.product_title}</p>
        <p className="text-[10px] text-muted-foreground">{relTime(item.created_at)}</p>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 24, x: -12 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ type: "spring", damping: 22, stiffness: 260 }}
        className="fixed bottom-20 left-4 z-30 max-w-[320px] rounded-2xl bg-black/85 backdrop-blur border border-primary/30 shadow-lg pl-3 pr-8 py-2.5"
        data-testid="live-order-toast"
      >
        {item.product_slug ? (
          <Link to={`/product/${item.product_slug}`} className="block">{Content}</Link>
        ) : Content}
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1.5 right-1.5 text-white/40 hover:text-white/80"
          aria-label="Dismiss"
          data-testid="live-order-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
